import React, { useState, useEffect } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import campaignService from '../services/campaignService';

interface AICopyEditorToolProps {
  newsletterEdition: any;
  onTemplateUpdated: () => void;
}

const AICopyEditorTool: React.FC<AICopyEditorToolProps> = ({ newsletterEdition, onTemplateUpdated }) => {
  const [inputMode, setInputMode] = useState<'type' | 'voice'>('type');
  const [prompt, setPrompt] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Sync voice transcript into prompt
  useEffect(() => {
    if (inputMode === 'voice' && transcript) {
      setPrompt(transcript);
    }
  }, [transcript, inputMode]);

  const handleApplyEdits = async () => {
    const editPrompt = prompt.trim();
    if (!editPrompt) return;

    setIsApplying(true);
    setStatusMessage(null);
    try {
      await campaignService.aiCopyEdit(newsletterEdition.id, editPrompt);
      setStatusMessage('Copy edits applied! Refreshing editor...');
      setPrompt('');
      resetTranscript();
      onTemplateUpdated();
    } catch (error: any) {
      console.error('Error applying AI copy edits:', error);
      const errorMsg = error.response?.data?.error || 'Failed to apply copy edits. Please try again.';
      setStatusMessage(`Error: ${errorMsg}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleUndo = async () => {
    if (!window.confirm('Undo the last AI edit? This will restore from the auto-backup.')) {
      return;
    }
    setIsUndoing(true);
    setStatusMessage(null);
    try {
      await campaignService.restoreEmailTemplate(newsletterEdition.id);
      setStatusMessage('Restored! Reloading...');
      window.location.reload();
    } catch (error: any) {
      console.error('Error undoing AI edit:', error);
      const errorMsg = error.response?.data?.error || 'Failed to undo. No backup available.';
      setStatusMessage(`Error: ${errorMsg}`);
      setIsUndoing(false);
    }
  };

  const toggleVoice = () => {
    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }}>
      <h3 style={{
        fontSize: '1.1em',
        fontWeight: '600',
        color: '#333',
      }}>
        AI Copy Editor
      </h3>

      {/* Input mode toggle */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={() => {
            setInputMode('type');
            if (listening) SpeechRecognition.stopListening();
          }}
          style={{
            flex: 1,
            padding: '6px 12px',
            backgroundColor: inputMode === 'type' ? '#1976D2' : '#f0f0f0',
            color: inputMode === 'type' ? 'white' : '#666',
            border: `1px solid ${inputMode === 'type' ? '#1565C0' : '#ccc'}`,
            borderRadius: '4px 0 0 4px',
            cursor: 'pointer',
            fontSize: '0.85em',
            fontWeight: '500',
          }}
        >
          Type
        </button>
        <button
          onClick={() => setInputMode('voice')}
          disabled={!browserSupportsSpeechRecognition}
          style={{
            flex: 1,
            padding: '6px 12px',
            backgroundColor: inputMode === 'voice' ? '#1976D2' : '#f0f0f0',
            color: inputMode === 'voice' ? 'white' : '#666',
            border: `1px solid ${inputMode === 'voice' ? '#1565C0' : '#ccc'}`,
            borderRadius: '0 4px 4px 0',
            cursor: browserSupportsSpeechRecognition ? 'pointer' : 'not-allowed',
            fontSize: '0.85em',
            fontWeight: '500',
          }}
        >
          Voice
        </button>
      </div>

      {/* Type mode: textarea */}
      {inputMode === 'type' && (
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe your edits (e.g., 'Make all copy more casual and conversational', 'Fix any grammar issues', 'Shorten all paragraphs to 2 sentences')"
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
      )}

      {/* Voice mode: mic button + transcript */}
      {inputMode === 'voice' && (
        <div>
          <button
            onClick={toggleVoice}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: listening ? '#f44336' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em',
              fontWeight: '500',
              marginBottom: '8px',
            }}
          >
            {listening ? 'Stop Recording' : 'Start Recording'}
          </button>
          {prompt && (
            <div style={{
              padding: '8px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              fontSize: '0.9em',
              color: '#333',
              minHeight: '40px',
            }}>
              {prompt}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleApplyEdits}
          disabled={isApplying || isUndoing || !prompt.trim()}
          style={{
            flex: 2,
            padding: '10px 16px',
            backgroundColor: isApplying || !prompt.trim() ? '#f8f8f8' : '#FF5722',
            border: `1px solid ${isApplying || !prompt.trim() ? '#ddd' : '#E64A19'}`,
            borderRadius: '6px',
            cursor: isApplying || isUndoing || !prompt.trim() ? 'not-allowed' : 'pointer',
            fontSize: '0.95em',
            color: isApplying || !prompt.trim() ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isApplying && !isUndoing && prompt.trim()) {
              e.currentTarget.style.backgroundColor = '#E64A19';
            }
          }}
          onMouseLeave={(e) => {
            if (!isApplying && !isUndoing && prompt.trim()) {
              e.currentTarget.style.backgroundColor = '#FF5722';
            }
          }}
        >
          {isApplying ? 'Applying...' : 'Apply Edits'}
        </button>
        <button
          onClick={handleUndo}
          disabled={isUndoing || isApplying}
          style={{
            flex: 1,
            padding: '10px 16px',
            backgroundColor: isUndoing ? '#f8f8f8' : '#FF9800',
            border: `1px solid ${isUndoing ? '#ddd' : '#F57C00'}`,
            borderRadius: '6px',
            cursor: isUndoing || isApplying ? 'not-allowed' : 'pointer',
            fontSize: '0.95em',
            color: isUndoing ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isUndoing && !isApplying) {
              e.currentTarget.style.backgroundColor = '#F57C00';
            }
          }}
          onMouseLeave={(e) => {
            if (!isUndoing && !isApplying) {
              e.currentTarget.style.backgroundColor = '#FF9800';
            }
          }}
        >
          {isUndoing ? 'Undoing...' : 'Undo'}
        </button>
      </div>

      {/* Status message */}
      {statusMessage && (
        <p style={{
          fontSize: '0.9em',
          color: statusMessage.startsWith('Error') ? '#f44336' : '#4CAF50',
          fontWeight: '500',
          textAlign: 'center',
          padding: '8px',
          backgroundColor: statusMessage.startsWith('Error') ? '#ffebee' : '#e8f5e9',
          borderRadius: '4px',
        }}>
          {statusMessage}
        </p>
      )}
    </div>
  );
};

export default AICopyEditorTool;
